import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet } from 'react-native';

import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { CasinoListSkeleton } from '@/feature/lobby-casino/components/Layout/ListSkeleton';
import { VerticalListLayout } from '@/feature/lobby-casino/components/Layout/VerticalListLayout';
import {
    CASINO_MIN_SEARCH_LENGTH,
    useCasinoGamesQuery,
    useFilteredLobbyGames,
} from '@/feature/lobby-casino/hooks/use-casino-games';

import { useSearchScreen } from '../screens/SearchScreenShell';

export const CasinoSearchResults = () => {
    const { query, debouncedQuery } = useSearchScreen();
    const { t } = useTranslation('casino');

    // debouncedQuery is already gated by CASINO_MIN_SEARCH_LENGTH in the shell
    const isValidSearchQuery = debouncedQuery.length > 0;

    const { data: lobbyData, isLoading } = useFilteredLobbyGames('Lobby');
    const {
        data: searchResults,
        isLoading: isSearchLoading,
        isFetching,
    } = useCasinoGamesQuery({
        name: debouncedQuery,
    });

    const featuredGames = useMemo(() => {
        if (!lobbyData || lobbyData.length === 0) return [];
        const featured = lobbyData.find(category => category.name?.toLowerCase().includes('featured games'));
        return featured?.items ?? [];
    }, [lobbyData]);

    const displayData = useMemo(() => {
        if (isValidSearchQuery) {
            return searchResults?.pages?.flatMap(page => page.items) ?? [];
        }
        return featuredGames;
    }, [featuredGames, searchResults, isValidSearchQuery]);

    return (
        <>
            {query.length < CASINO_MIN_SEARCH_LENGTH || displayData.length === 0 ? (
                <ScrollView
                    contentContainerStyle={styles.suggestedGamesContainer}
                    keyboardDismissMode="on-drag"
                    keyboardShouldPersistTaps="handled"
                >
                    {!isLoading && featuredGames.length > 0 ? (
                        <Box px="s16">
                            <Text color={ds.colorToken.text.primary} variant="bodyMedium2" fontWeight="bold" mb="s12">
                                {t('suggested_games')}
                            </Text>
                            <VerticalListLayout
                                data={featuredGames}
                                layoutHeaderProps={{
                                    categoryId: 'featured',
                                }}
                            />
                        </Box>
                    ) : null}
                </ScrollView>
            ) : null}

            {query && isFetching ? <CasinoListSkeleton /> : null}

            {query && isValidSearchQuery ? (
                <ScrollView
                    contentContainerStyle={styles.suggestedGamesContainer}
                    keyboardDismissMode="on-drag"
                    keyboardShouldPersistTaps="handled"
                >
                    {!isSearchLoading && displayData.length > 0 ? (
                        <Box px="s16">
                            <VerticalListLayout
                                data={displayData}
                                layoutHeaderProps={{
                                    categoryId: 'search',
                                }}
                            />
                        </Box>
                    ) : null}
                </ScrollView>
            ) : null}
        </>
    );
};

const styles = StyleSheet.create({
    suggestedGamesContainer: {
        paddingTop: 8,
    },
});
